import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { isPlatformBrowser } from '@angular/common';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Range Area Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-range-area-graph',
  standalone: false,
  templateUrl: './range-area-graph.component.html',
  styleUrl: './range-area-graph.component.scss'
})
export class RangeAreaGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private rangeAreaGraphReportSubscription: any;
  private isRangeAreaViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.rangeAreaGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Range Area Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isRangeAreaViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isRangeAreaViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Range Area Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isRangeAreaViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Range Area Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  /**
   * Create chart with legacy PHP implementation features
   * @param data - Chart data
   */
  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Range Area Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('chartdiv');
    if (!chartContainer) {
      console.error('Range Area Chart - Chart container not found! Make sure element with id "chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Range Area Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Range Area Chart - Creating new chart...');
      // Create root element
      this.root = this._baseChartService.createRoot('chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'range-area',
        data: data
      });

      // Dynamically determine category and value fields from data
      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      // Create chart container
      const chart = this._baseChartService.createChartContainer(this.root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: this.root.verticalLayout
      });

      // Set up color palette for consistent colors
      this._baseChartService.setupColorPalette(chart);

      // Create category axis (X-axis) like legacy
      const categoryAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 20,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45

      });

      // Create value axis (Y-axis) like legacy
      const valueAxis = this._baseChartService.createValueAxis(chart, {
        axisType: 'y'
      });

      // Set data
      categoryAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((valueField, index) => {
        let series = chart.series.push(
          am5xy.SmoothedXLineSeries.new(this.root, {
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            name: valueField,
            tension: 0.8,
            sequencedInterpolation: true,
          })
        );

        // Configure series with range area appearance like legacy
        series.fills.template.setAll({
          fillOpacity: 0.3,
          visible: true,
          //fill: am5.color(0x67b7dc)
        });

        // Configure tooltip properly with enhanced animation
        const tooltip = this._baseChartService.createStandardTooltip(this.root, 'area', {
          showValue: true,
          showCategory: true,
          showSeries: true
        });
        series.set('tooltip', tooltip);

        // Set data
        series.data.setAll(data);
      });



      /* valueFields.forEach((valueField, index) => {
        // Top series (high)
        const highSeries = chart.series.push(
          am5xy.SmoothedXLineSeries.new(this.root, {
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            name: `${valueField} High`,
            tension: 0.8,
            sequencedInterpolation: true,
          })
        );
       
        highSeries.fills.template.setAll({
          fillOpacity: 0.3,
          visible: true,
          fill: am5.color(0x67b7dc)
        });
       
        const tooltip = this._baseChartService.createStandardTooltip(this.root, 'area', {
          showValue: true,
          showCategory: true,
          showSeries: true
        });
        highSeries.set("tooltip", tooltip);
       
        // Bottom series (low) - only needed for area fill boundary
        const lowSeries = chart.series.push(
          am5xy.SmoothedXLineSeries.new(this.root, {
            xAxis: categoryAxis,
            yAxis: valueAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            name: `${valueField} Low`,
            tension: 0.8,
            sequencedInterpolation: true,
          })
        );
       
        // Set same data to both
        highSeries.data.setAll(data);
        lowSeries.data.setAll(data);
       
        // Link top series to bottom series for range fill
        // highSeries.stacked = true;
      }); */

      // Add cursor
      chart.set('cursor', am5xy.XYCursor.new(this.root, {
        xAxis: categoryAxis
      }));

      // Add legend
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Configure legend markers
      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      // Set legend data
      legend.data.setAll(chart.series.values);

      console.log('Range Area Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating range area chart:', error);
      this.isCreatingChart = false;
    }
  }

  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Range Area Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('chartdiv');
    if (!chartContainer) {
      console.log('Range Area Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Range Area Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.rangeAreaGraphReportSubscription) {
      this.rangeAreaGraphReportSubscription.unsubscribe();
    }
  }
}
